import React, { useCallback, useEffect, useRef, useState } from 'react'
import { currentDate, daysOfWeek } from './constants'
import {
  MonthManager,
  CalendarModalContainer,
  PreDefinedPeriodContainer,
  MonthDaysContainer,
  DaysStringContainer,
  DaysNumbersContainer,
  DaysNumber,
  CalendarButton,
  ClearButton
} from './styles'
import * as utils from './utils'
import { IconComponent } from '~/assets/icons/component'

interface ButtonsDefinedPeriodsProps {
  label: string
  returnInMonths: number
}

export interface DatePickerRangeProps {
  setPeriod: (PeriodSelected: Date[]) => void
  date?: Date[]
  buttonsDefinedPeriods?: ButtonsDefinedPeriodsProps[]
}

/**
 * This component will render a date picker calendar with the days available and pre defined periods buttons
 * @param {DatePickerRangeProps['setPeriod']} setPeriod - function to set the period selected
 * @param {DatePickerRangeProps['date']=} date - Tuple of range of dates selected
 * @param {DatePickerRangeProps['buttonsDefinedPeriods']=} buttonsDefinedPeriods - label and returnInMonths of the buttons
 * @returns {JSX.Element} return the rendered DatePicker
 * @example
 * const [date, setDate] = useState<Date[]>([])
 * <DatePickerRange setPeriod={setDate} date={date} />
 */

export const DatePickerRange = ({ setPeriod, date, buttonsDefinedPeriods }: DatePickerRangeProps): JSX.Element => {
  const initialDate = date && date.length > 0 ? date : [currentDate]
  const [predefinedPeriodSelected, setPredefinedPeriodSelected] = useState<string>('')
  const [dayInFocus, setDayInFocus] = useState<Date | null>(
    initialDate.length > 1 ? initialDate[initialDate.length - 1] : null
  )
  const [mainVisiblePeriod, setMainVisiblePeriod] = useState<Date>(initialDate[initialDate.length - 1])
  const [selectedPeriod, setSelectedPeriod] = useState<Date[]>(initialDate.length > 1 ? initialDate : [])
  const daysRef = useRef<HTMLButtonElement[]>([])

  useEffect(() => {
    const hasExternalDateReset = date && date.length === 0

    if (hasExternalDateReset) {
      setPredefinedPeriodSelected('')
      setSelectedPeriod([])
      setDayInFocus(null)
    }
  }, [date])

  const submitCalendar = (submittedPeriod: Date[]) => {
    setPeriod(submittedPeriod)
  }

  const handlePreDefinedPeriodInMonths = (buttonsDefinedPeriods: ButtonsDefinedPeriodsProps) => {
    const actualDate = new Date()
    const previousDate = utils.getPreviousDateWithMonthPeriod(actualDate, buttonsDefinedPeriods.returnInMonths)
    setDayInFocus(null)
    if (predefinedPeriodSelected === buttonsDefinedPeriods.label) {
      setPredefinedPeriodSelected('')
      submitCalendar([])
      return
    }
    setSelectedPeriod([])
    setPredefinedPeriodSelected(buttonsDefinedPeriods.label)
    submitCalendar(utils.setHoursInRangeDates(previousDate, actualDate))
  }

  const handleClearPeriodSelected = () => {
    setPredefinedPeriodSelected('')
    setSelectedPeriod([])
    setDayInFocus(null)
    submitCalendar([])
  }

  const backwardPeriodInMonthsToUpdateCalendar = useCallback(
    (monthQuantity = 1) => {
      const previousDate = utils.getPreviousDateWithMonthPeriod(mainVisiblePeriod, monthQuantity)
      setMainVisiblePeriod(previousDate)
    },
    [mainVisiblePeriod]
  )

  const forwardPeriodInMothsToUpdateCalendar = useCallback(
    (monthQuantity = 1) => {
      const futureDate = utils.getNextDateWithMonthPeriod(mainVisiblePeriod, monthQuantity)
      setMainVisiblePeriod(futureDate)
    },
    [mainVisiblePeriod]
  )

  const focusForwardPeriodInDays = useCallback(
    (daysQuantity: number) => {
      if (dayInFocus) {
        const nextPeriodInFocus = new Date(new Date(dayInFocus).setDate(dayInFocus.getDate() + daysQuantity))
        if (mainVisiblePeriod.getMonth() < nextPeriodInFocus.getMonth()) {
          forwardPeriodInMothsToUpdateCalendar(1)
        }
        setDayInFocus(nextPeriodInFocus)
      }
    },
    [dayInFocus, forwardPeriodInMothsToUpdateCalendar, mainVisiblePeriod]
  )

  const focusBackwardPeriodInDays = useCallback(
    (daysQuantity: number) => {
      if (dayInFocus) {
        const previousPeriodInFocus = new Date(new Date(dayInFocus).setDate(dayInFocus.getDate() - daysQuantity))
        if (mainVisiblePeriod.getMonth() > previousPeriodInFocus.getMonth()) {
          backwardPeriodInMonthsToUpdateCalendar(1)
        }
        setDayInFocus(previousPeriodInFocus)
      }
    },
    [backwardPeriodInMonthsToUpdateCalendar, dayInFocus, mainVisiblePeriod]
  )

  const changeMainMonthShortcutKeyboard = useCallback(
    (event: React.KeyboardEvent): void => {
      const { key } = event
      const shiftIsPressed = event.shiftKey

      switch (key) {
        case 'PageDown':
          event.preventDefault()
          return forwardPeriodInMothsToUpdateCalendar(shiftIsPressed ? 12 : 1)
        case 'PageUp':
          event.preventDefault()
          return backwardPeriodInMonthsToUpdateCalendar(shiftIsPressed ? 12 : 1)
      }
    },
    [backwardPeriodInMonthsToUpdateCalendar, forwardPeriodInMothsToUpdateCalendar]
  )

  const arrowShortcutKeyboard = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case 'ArrowUp':
        event.preventDefault()
        return focusBackwardPeriodInDays(7)
      case 'ArrowDown':
        event.preventDefault()
        return focusForwardPeriodInDays(7)
      case 'ArrowLeft':
        event.preventDefault()
        return focusBackwardPeriodInDays(1)
      case 'ArrowRight':
        event.preventDefault()
        return focusForwardPeriodInDays(1)
    }
  }

  const handleSelectDay = (day: Date) => {
    if (utils.verifyIfMonthIsDifferent(day, mainVisiblePeriod)) {
      day > mainVisiblePeriod ? forwardPeriodInMothsToUpdateCalendar() : backwardPeriodInMonthsToUpdateCalendar()
      return
    }

    setDayInFocus(new Date(day))
    setPredefinedPeriodSelected('')

    const newSelectedDates = utils.returnNewRangeDates(day, selectedPeriod)
    setSelectedPeriod(newSelectedDates)
    submitCalendar(newSelectedDates)
  }

  return (
    <CalendarModalContainer onKeyDown={(event: React.KeyboardEvent) => changeMainMonthShortcutKeyboard(event)}>
      <PreDefinedPeriodContainer>
        {buttonsDefinedPeriods?.map(buttonDefinedPeriod => {
          return (
            <CalendarButton
              key={buttonDefinedPeriod.label}
              className={predefinedPeriodSelected === buttonDefinedPeriod.label ? 'active' : 'inactive'}
              onClick={() => handlePreDefinedPeriodInMonths(buttonDefinedPeriod)}
            >
              {buttonDefinedPeriod.label}
            </CalendarButton>
          )
        })}
      </PreDefinedPeriodContainer>

      <ClearButton
        className={selectedPeriod.length > 0 || predefinedPeriodSelected !== '' ? 'active' : 'disable'}
        onClick={() => handleClearPeriodSelected()}
      >
        Limpar
      </ClearButton>

      <MonthManager>
        <button onClick={() => backwardPeriodInMonthsToUpdateCalendar()} data-testid="prev-arrow">
          <IconComponent name="chevronLeft" />
        </button>
        <span>{utils.createCalendarLabelDate(mainVisiblePeriod)}</span>
        <button onClick={() => forwardPeriodInMothsToUpdateCalendar()} data-testid="next-arrow">
          <IconComponent name="chevronRight" />
        </button>
      </MonthManager>

      <MonthDaysContainer>
        <DaysStringContainer>
          {daysOfWeek.map(day => {
            return (
              <span key={day} title={day}>
                {day.slice(0, 3)}
              </span>
            )
          })}
        </DaysStringContainer>
        <DaysNumbersContainer data-testid="days-container" onKeyDown={arrowShortcutKeyboard} tabIndex={0}>
          {utils.createDaysAvailableInPeriod(mainVisiblePeriod).map((day: Date, index: number) => {
            const isSelected = utils.verifyIfDayExists(day, selectedPeriod)
            const isDayOfOtherMonth = utils.verifyIfMonthIsDifferent(day, mainVisiblePeriod)
            const isDayBetweenDaysSelected = utils.verifyIfDayIsBetweenDates(day, selectedPeriod[0], selectedPeriod[1])

            if (dayInFocus?.getTime() === day.getTime() && daysRef.current[index]) {
              daysRef.current[index].setAttribute('tabindex', '0')
              daysRef.current[index].focus()
            }

            return (
              <DaysNumber
                ref={(buttonRef: HTMLButtonElement) => (daysRef.current[index] = buttonRef)}
                key={`${day.getTime()}`}
                onClick={() => handleSelectDay(day)}
                isDayOfOtherMonth={isDayOfOtherMonth}
                isBetweenDay={isDayBetweenDaysSelected}
                isSelectedDay={isSelected}
              >
                <span>{day.getDate()}</span>
              </DaysNumber>
            )
          })}
        </DaysNumbersContainer>
      </MonthDaysContainer>
    </CalendarModalContainer>
  )
}
